"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";
import { applyConsent } from "@/integrations/posthog/consent";

import { ConsentModal } from "./ConsentModal";
import { getConsent } from "./consent-storage";
import { useConsent } from "./useConsent";

export function ConsentBanner() {
  const t = useTranslations("consent.banner");
  const { state, ready, grantAll, denyAll } = useConsent();
  const [modalOpen, setModalOpen] = useState(false);

  // Apply the analytics decision on first mount so returning visitors keep
  // their choice across sessions. Reads localStorage directly to avoid racing
  // the consent snapshot — `applyConsent` no-ops if posthog hasn't loaded yet.
  // The marketing pixels re-apply their own state in `TikTokPixel`,
  // `MetaPixel` and `GoogleAdsTag`.
  //
  // A visitor with NO decision yet falls through to "denied", which under
  // `cookieless_mode: 'on_reject'` means "measure cookielessly, store nothing"
  // rather than "measure nothing". This call is also what reliably kicks
  // cookieless capture into life given posthog-js#2841, so it must run for the
  // no-decision case too — not just when a stored choice exists.
  useEffect(() => {
    applyConsent(getConsent()?.analytics ?? "denied");
  }, []);

  // Don't render until we've read localStorage on the client; otherwise the
  // SSR pass would emit the banner and it would flicker for users who have
  // already decided.
  if (!ready) return null;

  const showBanner = state === null;

  return (
    <>
      {showBanner ? (
        <div
          role="region"
          aria-label={t("title")}
          className="fixed inset-x-0 bottom-0 z-50 border-t border-paper-3 bg-paper/95 backdrop-blur shadow-[0_-8px_24px_-12px_rgba(30,26,25,0.2)]"
        >
          <div className="mx-auto flex max-w-5xl flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:gap-4 sm:py-3">
            <div className="flex flex-1 flex-col gap-1">
              <p className="text-sm font-semibold text-ink">{t("title")}</p>
              <p className="text-xs leading-relaxed text-ink-2">
                {t("body")}{" "}
                <Link
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  href={"/privacy" as any}
                  className="underline underline-offset-2 hover:text-ink"
                >
                  {t("policyLink")}
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2 sm:flex-nowrap">
              <Button variant="ghost" size="sm" onClick={() => setModalOpen(true)}>
                {t("settings")}
              </Button>
              <Button variant="outline" size="sm" onClick={denyAll}>
                {t("decline")}
              </Button>
              <Button size="sm" onClick={grantAll}>
                {t("accept")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <ConsentModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
